import React, { useContext, useEffect, useState, useCallback } from 'react'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { CheckinTabContainer } from '@/components/organisms/settings/checkin-tab-container'
import { TabContainer } from '@/components/organisms/settings/tab-container'
import { putSelfCheckinSearchSettingIsEnabled, putSelfCheckinSetting, fetchSelfCheckinSetting } from '@/apis/aipass'
import { AccountContext } from '@/contexts/account'
import { RadioGroupField } from '@/components/molecules/radio-group-field'
import { ToggleButton } from '@/components/molecules/settings/toggle-button'
import { Hint } from '@/components/atoms/hint'
import { putSelfCheckinSearchSettingSortOrder } from '@/apis/aipass'
import { EditFooter } from '@/components/organisms/edit-footer'
import { LoaderContextCreator } from '@/contexts/loader'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useSearchItemsManagement } from '@/hooks/use-search-items-management'
import { SearchItemsSection } from '@/components/organisms/settings/search-items-section'
import { SearchSetting } from '@/models/search-setting'
import { defaultSelfCheckinSetting, RoomOptionDisplayType, SelfCheckinSetting } from '@/models/self-checkin/setting'
import { setHasSelfCheckinPlugin } from '@/libs/plugins'

const settingPanes = ['Common', 'Pre-checkin', 'Self check-in'] as const
type SettingPane = (typeof settingPanes)[number]
type CustomInputUpdate = {
  [InputName in keyof SelfCheckinSetting]: {
    inputName: InputName
    changeValue: SelfCheckinSetting[InputName]
  }
}[keyof SelfCheckinSetting]

type SettingPaneNavigationProps = {
  panes: readonly SettingPane[]
  currentPane: SettingPane
  onSelect: (pane: SettingPane) => void
}

const SettingPaneNavigation: React.FC<SettingPaneNavigationProps> = ({ panes, currentPane, onSelect }) => {
  const { t } = useTranslation()

  return (
    <nav css={settingPaneNavigationStyle} aria-label={t('Checkin')} role="tablist" aria-orientation="vertical">
      {panes.map(pane => (
        <button
          key={pane}
          type="button"
          css={[settingPaneNavigationItemStyle, pane === currentPane && settingPaneNavigationItemActiveStyle]}
          role="tab"
          aria-selected={pane === currentPane}
          onClick={() => onSelect(pane)}
        >
          {t(pane)}
        </button>
      ))}
    </nav>
  )
}

export const SelfCheckin: React.FC = () => {
  const { t } = useTranslation()
  const { account, plugins } = useContext(AccountContext)
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { errorHandler } = useErrorHandler()
  const [currentPane, setCurrentPane] = useState<SettingPane>('Common')
  // セルフチェックインの項目は現地タブレットの設定で、ゲストアプリの事前チェックインでは使わないため、プラグインが無い施設には出さない
  const visiblePanes = setHasSelfCheckinPlugin(plugins) ? settingPanes : settingPanes.filter(pane => pane !== 'Self check-in')

  // カスタムインプット状態管理
  const [customInput, setCustomInput] = useState<SelfCheckinSetting>(defaultSelfCheckinSetting)

  // 検索項目の共通Hook
  const {
    formSearchItems,
    defaultSearchItems,
    setDefaultSearchItems,
    reset,
    isOrderChanged,
    setIsOrderChanged,
    onCancel,
    dragStart,
    dragOver,
    dragDrop,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = useSearchItemsManagement({
    initialSearchItems: [],
  })

  // 検索項目トグル変更時の処理
  const toggleSearchItem = useCallback(
    async (i: number, searchItem: SearchSetting) => {
      setIsLoading(true)
      const newIsEnabled = !searchItem.isEnabled
      try {
        await putSelfCheckinSearchSettingIsEnabled({
          inputType: searchItem.inputType,
          isEnabled: newIsEnabled,
        })
        formSearchItems.update(i, { ...searchItem, isEnabled: newIsEnabled })
        setDefaultSearchItems(
          defaultSearchItems.map(item => (item.inputType === searchItem.inputType ? { ...item, isEnabled: newIsEnabled } : item)),
        )
      } catch (e) {
        errorHandler(e)
      } finally {
        setIsLoading(false)
      }
    },
    [formSearchItems, defaultSearchItems, setIsLoading, errorHandler],
  )

  // カスタムインプット変更時の処理
  const changeCustomInput = useCallback(
    async (props: CustomInputUpdate) => {
      try {
        setIsLoading(true)
        setCustomInput(prev => ({ ...prev, [props.inputName]: props.changeValue }))
        await putSelfCheckinSetting({ [props.inputName]: props.changeValue })
      } catch (e) {
        errorHandler(e)
      } finally {
        setIsLoading(false)
      }
    },
    [setIsLoading, errorHandler],
  )

  // 検索項目順序変更時の処理
  const onSaveSortOrder = async () => {
    try {
      setIsLoading(true)
      await putSelfCheckinSearchSettingSortOrder({ searchItems: formSearchItems.fields })
      // キャンセル時に順序を戻すためのデフォルト値を更新
      setDefaultSearchItems(formSearchItems.fields)
      setIsOrderChanged(false)
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!account) {
      return
    }
    setIsLoading(true)
    fetchSelfCheckinSetting(account.hotel.id)
      .then(settings => {
        if (!settings) {
          return
        }
        const { searchItems, ...input } = settings
        setCustomInput({ ...defaultSelfCheckinSetting, ...input })
        setDefaultSearchItems(searchItems)
        reset({ searchItems })
      })
      .finally(() => setIsLoading(false))
  }, [account])

  const footerContent = isOrderChanged ? <EditFooter onSave={onSaveSortOrder} onCancel={onCancel} /> : undefined

  return (
    <SettingsLayout loading={false} footerContent={footerContent}>
      <TabContainer
        tabComponent={<CheckinTabContainer currentTab="Checkin" />}
        customStyle={settingPaneContainerStyle}
        isShowFooter={isOrderChanged}
      >
        <SettingPaneNavigation panes={visiblePanes} currentPane={currentPane} onSelect={setCurrentPane} />
        <div css={settingPaneContentStyle}>
          {currentPane === 'Common' && (
            <>
              <div css={infoHeaderContainerStyle}>
                <div css={infoHeaderTitleStyle}>{t('Identity verification')}</div>
              </div>
              <div css={mainContainerStyle}>
                <div css={facilityRecommendStyle}>
                  <div css={facilityInfoListContainerStyle}>
                    <div css={facilityInfoListNameStyle}>{t('Japanese nationality')}</div>
                    <ToggleButton
                      value={customInput.requiredIdentify}
                      onChange={() => changeCustomInput({ inputName: 'requiredIdentify', changeValue: !customInput.requiredIdentify })}
                    />
                  </div>
                  <div css={facilityInfoListContainerStyle}>
                    <div css={facilityInfoListNameStyle}>{t('Foreign nationality')}</div>
                    <ToggleButton
                      value={customInput.requiredForeignIdentify}
                      onChange={() =>
                        changeCustomInput({ inputName: 'requiredForeignIdentify', changeValue: !customInput.requiredForeignIdentify })
                      }
                    />
                  </div>
                  <div css={facilityInfoListContainerStyle}>
                    <div css={facilityInfoListNameStyle} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {t('ID card/passport verification')}
                      <Hint customCss={helpPopupStyle}>
                        <div>
                          {t('If you select “Re-shoot required” you will need to re-shoot until your ID card/passport is recognized.')}
                          <br />
                          {t(
                            'At unmanned facilities, we recommend selecting “Keep going” in case your ID card/passport cannot be recognized.',
                          )}
                        </div>
                      </Hint>
                    </div>
                    <RadioGroupField
                      value={customInput.requiredIdentifyVerification ? '1' : '0'}
                      items={[
                        { label: t('Re-shoot required'), value: '1' },
                        { label: t('Keep going'), value: '0' },
                      ]}
                      onChange={() =>
                        changeCustomInput({
                          inputName: 'requiredIdentifyVerification',
                          changeValue: !customInput.requiredIdentifyVerification,
                        })
                      }
                      groupCss={{ gap: 32, overflow: 'visible' }}
                      itemWrapperCss={{ width: '50%' }}
                      itemCss={{ fontWeight: 'normal', width: 'max-content' }}
                      style={{ radioSize: 18 }}
                    />
                  </div>
                </div>
              </div>

              <div css={infoHeaderContainerStyle}>
                <div css={infoHeaderTitleStyle}>{t('Accompany name')}</div>
              </div>
              <div css={mainContainerStyle}>
                <div css={facilityRecommendStyle}>
                  <div css={facilityInfoListContainerStyle}>
                    <div css={facilityInfoListNameStyle}>{t('Accompany name input')}</div>
                    <ToggleButton
                      value={customInput.requiredAccompanyNameInput}
                      onChange={() =>
                        changeCustomInput({ inputName: 'requiredAccompanyNameInput', changeValue: !customInput.requiredAccompanyNameInput })
                      }
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {currentPane === 'Pre-checkin' && (
            <>
              <div css={infoHeaderContainerStyle}>
                <div css={infoHeaderTitleStyle}>{t('Identity verification')}</div>
              </div>
              <div css={mainContainerStyle}>
                <div css={facilityRecommendStyle}>
                  <div css={facilityInfoListContainerStyle}>
                    <div css={facilityInfoListNameStyle} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {t('Performing identity verification')}
                      <Hint customCss={preCheckinHelpPopupStyle}>
                        {t(
                          'The target is either overseas residents only, or both Japanese and overseas residents, as configured under “Common”.',
                        )}
                      </Hint>
                    </div>
                    <RadioGroupField
                      value={customInput.requiredPreCheckinIdentify ? '1' : '0'}
                      items={[
                        { label: t('Also at pre-checkin'), value: '1' },
                        { label: t('On-site only'), value: '0' },
                      ]}
                      onChange={e =>
                        changeCustomInput({
                          inputName: 'requiredPreCheckinIdentify',
                          changeValue: e.target.value === '1',
                        })
                      }
                      groupCss={preCheckinRadioGroupStyle}
                      itemWrapperCss={preCheckinRadioItemWrapperStyle}
                      itemCss={{ fontWeight: 'normal', width: 'max-content' }}
                      style={{ radioSize: 18 }}
                    />
                  </div>

                  {Boolean(customInput.requiredPreCheckinIdentify) && (
                    <div css={facilityInfoListContainerStyle}>
                      <div css={facilityInfoListNameStyle}>{t('Identity verification skip settings')}</div>
                      <RadioGroupField
                        value={customInput.isPreCheckinIdentifySkippable ? '1' : '0'}
                        items={[
                          { label: t('Skip not allowed'), value: '0' },
                          { label: t('Skip allowed'), value: '1' },
                        ]}
                        onChange={e =>
                          changeCustomInput({
                            inputName: 'isPreCheckinIdentifySkippable',
                            changeValue: e.target.value === '1',
                          })
                        }
                        groupCss={preCheckinRadioGroupStyle}
                        itemWrapperCss={preCheckinRadioItemWrapperStyle}
                        itemCss={{ fontWeight: 'normal', width: 'max-content' }}
                        style={{ radioSize: 18 }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {currentPane === 'Self check-in' && (
            <>
              <SearchItemsSection
                searchItems={formSearchItems.fields}
                onToggle={toggleSearchItem}
                onDragStart={dragStart}
                onDragOver={dragOver}
                onDrop={dragDrop}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                customTitleStyle={infoHeaderTitleStyle}
                customToggleLabelStyle={toggleLabelStyle}
                hint="Select the search criteria to use at checkin and set the display order."
              />

              <div css={infoHeaderContainerStyle}>
                <div css={[infoHeaderTitleStyle, { display: 'flex', alignItems: 'center', gap: 8 }]}>
                  {t('Guest information input')}
                  <Hint customCss={helpPopupStyle}>
                    {t('Name, address, and contact information are required by the Ryokan Business Law')}
                  </Hint>
                </div>
              </div>
              <div css={mainContainerStyle}>
                <div css={facilityRecommendStyle}>
                  <div css={facilityInfoListContainerStyle}>
                    <div css={facilityInfoListNameStyle}>{t('Furigana (for Japanese nationals)')}</div>
                    <ToggleButton
                      value={customInput.requiredFuriganaInput}
                      onChange={() =>
                        changeCustomInput({ inputName: 'requiredFuriganaInput', changeValue: !customInput.requiredFuriganaInput })
                      }
                      label={{ on: t('Required'), off: t('Optional') }}
                    />
                  </div>

                  <div css={facilityInfoListContainerStyle}>
                    <div css={facilityInfoListNameStyle}>{t('Date of birth')}</div>
                    <ToggleButton
                      value={customInput.requiredBirthDateInput}
                      onChange={() =>
                        changeCustomInput({ inputName: 'requiredBirthDateInput', changeValue: !customInput.requiredBirthDateInput })
                      }
                      label={{ on: t('Required'), off: t('Optional') }}
                    />
                  </div>

                  <div css={facilityInfoListContainerStyle}>
                    <div css={facilityInfoListNameStyle}>{t('Sex')}</div>
                    <ToggleButton
                      value={customInput.requiredGenderInput}
                      onChange={() =>
                        changeCustomInput({ inputName: 'requiredGenderInput', changeValue: !customInput.requiredGenderInput })
                      }
                      label={{ on: t('Required'), off: t('Optional') }}
                    />
                  </div>

                  <div css={facilityInfoListContainerStyle}>
                    <div css={facilityInfoListNameStyle}>{t('Phone number')}</div>
                    <ToggleButton
                      value={customInput.requiredTelephoneInput}
                      onChange={() =>
                        changeCustomInput({ inputName: 'requiredTelephoneInput', changeValue: !customInput.requiredTelephoneInput })
                      }
                      label={{ on: t('Required'), off: t('Optional') }}
                    />
                  </div>

                  <div css={facilityInfoListContainerStyle}>
                    <div css={facilityInfoListNameStyle}>{t('Email address')}</div>
                    <ToggleButton
                      value={customInput.requiredEmailInput}
                      onChange={() => changeCustomInput({ inputName: 'requiredEmailInput', changeValue: !customInput.requiredEmailInput })}
                      label={{ on: t('Required'), off: t('Optional') }}
                    />
                  </div>

                  <div css={facilityInfoListContainerStyle}>
                    <div css={facilityInfoListNameStyle}>{t('Address')}</div>
                    <ToggleButton
                      value={customInput.requiredAddressInput}
                      onChange={() =>
                        changeCustomInput({ inputName: 'requiredAddressInput', changeValue: !customInput.requiredAddressInput })
                      }
                      label={{ on: t('Required'), off: t('Optional') }}
                    />
                  </div>
                </div>
              </div>

              <div css={infoHeaderContainerStyle}>
                <div css={infoHeaderTitleStyle}>{t('Multi-Room / Multi-Reservation Check-in')}</div>
              </div>
              <div css={mainContainerStyle}>
                <div css={facilityRecommendStyle}>
                  <div css={facilityInfoListContainerStyle}>
                    <div css={facilityInfoListNameStyle} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {t('Multi-Room / Multi-Reservation Check-in')}
                      <Hint customCss={helpPopupStyle}>
                        <div>
                          {t('Choose what to display for room options.')}
                          <br />
                          {t('Showing the room representative name helps guests find their room more easily.')}
                        </div>
                      </Hint>
                    </div>
                    <RadioGroupField
                      value={customInput.roomOptionDisplayType}
                      items={[
                        { label: t('Guest name & Guests per room'), value: RoomOptionDisplayType.GuestName },
                        { label: t('Room representative name'), value: RoomOptionDisplayType.RoomAssignName },
                      ]}
                      onChange={e => changeCustomInput({ inputName: 'roomOptionDisplayType', changeValue: e.target.value })}
                      groupCss={{ gap: 32, overflow: 'visible' }}
                      itemWrapperCss={{ width: '50%' }}
                      itemCss={{ fontWeight: 'normal', width: 'max-content' }}
                      style={{ radioSize: 18 }}
                    />
                  </div>
                </div>
              </div>

              <div css={infoHeaderContainerStyle}>
                <div css={infoHeaderTitleStyle}>{t('Check-in availability')}</div>
              </div>
              <div css={mainContainerStyle}>
                <div css={facilityRecommendStyle}>
                  <div css={facilityInfoListContainerStyle}>
                    <div css={facilityInfoListNameStyle} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {t('Rooms with uncleaned or cleaned status')}
                      <Hint customCss={helpPopupStyle}>
                        {t('This setting is effective when the Cleaning Management plugin is enabled.')}
                      </Hint>
                    </div>
                    <RadioGroupField
                      value={customInput.isCheckinAllowedBeforeRoomInspection ? '1' : '0'}
                      items={[
                        { label: t('Check-in unavailable'), value: '0' },
                        { label: t('Check-in available'), value: '1' },
                      ]}
                      onChange={() =>
                        changeCustomInput({
                          inputName: 'isCheckinAllowedBeforeRoomInspection',
                          changeValue: !customInput.isCheckinAllowedBeforeRoomInspection,
                        })
                      }
                      groupCss={{ gap: 32, overflow: 'visible' }}
                      itemWrapperCss={{ width: '50%' }}
                      itemCss={{ fontWeight: 'normal', width: 'max-content' }}
                      style={{ radioSize: 18 }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </TabContainer>
    </SettingsLayout>
  )
}

const settingPaneContainerStyle = css({
  boxSizing: 'border-box',
  flexDirection: 'row',
  paddingTop: 60,
  overflow: 'visible',
})

const settingPaneNavigationStyle = css({
  boxSizing: 'border-box',
  width: 180,
  flexShrink: 0,
  border: '1px solid #F2F2F2',
})

const settingPaneNavigationItemStyle = css({
  width: '100%',
  height: 44,
  padding: '0 24px',
  border: 0,
  borderBottom: '1px solid #F2F2F2',
  background: '#FFF',
  color: '#676767',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 'bold',
  textAlign: 'left',
  '&:hover': {
    background: '#F2F2F2',
  },
  '&:focus-visible': {
    outline: '2px solid #F2A40B',
    outlineOffset: -2,
  },
})

const settingPaneNavigationItemActiveStyle = css({
  background: '#F2F2F2',
})

const settingPaneContentStyle = css({
  width: 'calc(100% - 180px)',
  minWidth: 0,
  background: '#FFF',
  overflow: 'auto',
})

const mainContainerStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  color: '#272727',
  width: '100%',
  flexWrap: 'wrap',
})

const facilityRecommendStyle = css({ width: '100%' })

const facilityInfoListContainerStyle = css({
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  height: 62,
  borderBottom: '1px solid #F2F2F2',
  padding: '0 32px',
})

const facilityInfoListNameStyle = css({
  color: '#272727',
  fontSize: 14,
  fontWeight: 'bold',
  width: '100%',
  padding: '25px 0px',
  height: '100%',
  letterSpacing: '1.4px',
  p: {
    fontWeight: 'normal',
  },
})

const infoHeaderContainerStyle = css({
  background: '#F5F5F5',
  height: 32,
  padding: '0 32px',
  borderBottom: '1px solid #F2F2F2',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const infoHeaderTitleStyle = css({
  fontSize: 12,
  padding: '6px 0',
  fontWeight: 'bold',
})

const toggleLabelStyle = css({
  color: '#272727',
})

const helpPopupStyle = css({
  img: { width: 18 },
  '.help-popup': {
    color: '#676767',
    fontSize: 12,
    left: 23,
    top: (68 - 18) / -2,
    whiteSpace: 'nowrap',
    padding: 16,
    lineHeight: 1.5,
  },
})

const preCheckinHelpPopupStyle = css(helpPopupStyle, {
  '.help-popup': {
    width: 450,
    minHeight: 74,
    boxSizing: 'border-box',
    lineHeight: '21px',
    whiteSpace: 'normal',
  },
})

const preCheckinRadioGroupStyle = css({
  width: 330,
  flexShrink: 0,
  gap: 32,
  justifyContent: 'space-between',
  overflow: 'visible',
})

const preCheckinRadioItemWrapperStyle = css({
  display: 'flex',
})
